import { createSignal, type Accessor } from "solid-js";
import { ApiError } from "../api/client";
import type { ObservedUsage, ObservedUsageGroup, ObservedUsageWindow } from "../model";

export const observedUsageFreshnessMS = 30_000;

export type ObservedUsageQueryStatus =
  | "idle"
  | "loading"
  | "ready"
  | "refreshing"
  | "stale"
  | "unsupported"
  | "error";

export interface ObservedUsageQuery {
  window: ObservedUsageWindow;
  group: ObservedUsageGroup;
}

export interface ObservedUsageQueryState extends ObservedUsageQuery {
  status: ObservedUsageQueryStatus;
  data: ObservedUsage | null;
  error: unknown;
  updatedAt: number | null;
}

export interface ObservedUsageController {
  state: Accessor<ObservedUsageQueryState>;
  select: (query: ObservedUsageQuery) => Promise<ObservedUsage | null>;
  refresh: () => Promise<ObservedUsage | null>;
}

interface QueryEntry {
  status: ObservedUsageQueryStatus;
  data: ObservedUsage | null;
  error: unknown;
  updatedAt: number | null;
  inFlight: Promise<ObservedUsage | null> | null;
  requestID: number;
}

interface ObservedUsageControllerOptions {
  load: (window: ObservedUsageWindow, group: ObservedUsageGroup) => Promise<ObservedUsage>;
  now?: () => number;
  freshnessMS?: number;
}

function emptyEntry(): QueryEntry {
  return {
    status: "idle",
    data: null,
    error: null,
    updatedAt: null,
    inFlight: null,
    requestID: 0,
  };
}

export function createObservedUsageController(options: ObservedUsageControllerOptions): ObservedUsageController {
  const now = options.now ?? Date.now;
  const freshnessMS = options.freshnessMS ?? observedUsageFreshnessMS;
  const entries = new Map<ObservedUsageWindow, Map<ObservedUsageGroup, QueryEntry>>();
  const [selection, setSelection] = createSignal<ObservedUsageQuery>({
    window: "24h",
    group: "all",
  });
  const [revision, setRevision] = createSignal(0);
  let unsupported = false;

  function entryFor(query: ObservedUsageQuery): QueryEntry {
    let windowEntries = entries.get(query.window);
    if (!windowEntries) {
      windowEntries = new Map<ObservedUsageGroup, QueryEntry>();
      entries.set(query.window, windowEntries);
    }
    let entry = windowEntries.get(query.group);
    if (!entry) {
      entry = emptyEntry();
      windowEntries.set(query.group, entry);
    }
    return entry;
  }

  function notify(): void {
    setRevision((value) => value + 1);
  }

  const state: Accessor<ObservedUsageQueryState> = () => {
    revision();
    const current = selection();
    const entry = entryFor(current);
    return {
      window: current.window,
      group: current.group,
      status: unsupported ? "unsupported" : entry.status,
      data: unsupported ? null : entry.data,
      error: unsupported ? null : entry.error,
      updatedAt: unsupported ? null : entry.updatedAt,
    };
  };

  function isFresh(entry: QueryEntry): boolean {
    return entry.updatedAt !== null && now() - entry.updatedAt < freshnessMS;
  }

  function request(query: ObservedUsageQuery, force: boolean): Promise<ObservedUsage | null> {
    if (unsupported) return Promise.resolve(null);
    const entry = entryFor(query);
    if (entry.inFlight) return entry.inFlight;
    if (!force && entry.data !== null && entry.status === "ready" && isFresh(entry)) {
      return Promise.resolve(entry.data);
    }

    const requestID = ++entry.requestID;
    entry.status = entry.data === null ? "loading" : "refreshing";
    entry.error = null;
    notify();

    const pending = options.load(query.window, query.group)
      .then((data) => {
        if (entry.requestID !== requestID) return null;
        entry.status = "ready";
        entry.data = data;
        entry.error = null;
        entry.updatedAt = now();
        notify();
        return data;
      })
      .catch((error: unknown) => {
        if (entry.requestID !== requestID) return null;
        if (error instanceof ApiError && error.status === 404) {
          unsupported = true;
          entry.status = "unsupported";
          entry.data = null;
          entry.updatedAt = null;
          entry.error = null;
        } else {
          entry.status = entry.data === null ? "error" : "stale";
          entry.error = error;
        }
        notify();
        return null;
      })
      .finally(() => {
        if (entry.requestID === requestID) entry.inFlight = null;
      });
    entry.inFlight = pending;
    return pending;
  }

  function select(query: ObservedUsageQuery): Promise<ObservedUsage | null> {
    setSelection(query);
    return request(query, false);
  }

  function refresh(): Promise<ObservedUsage | null> {
    const current = selection();
    return request(current, true);
  }

  return {
    state,
    select,
    refresh,
  };
}
